import axios from "axios";
import React, { useEffect, useState } from "react";
import { ApiConnectionString } from "../../../constants/ApiConntection";
import { useParams } from "react-router-dom";
import { useAuthContext } from "../../../hooks/useAuthContext";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
  Gem,
  ArrowUpCircle,
  ArrowDownCircle,
  CheckCircle,
  BoxSeam,
  Globe,
} from "react-bootstrap-icons";

export default function ChangePlan() {
  const [sub, setSub] = useState(null);
  const [club, setClub] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isChanging, setIsChanging] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingTier, setPendingTier] = useState(null);

  const { id } = useParams();
  const { user } = useAuthContext();

  const fetchData = async () => {
    setIsLoading(true);
    setMessage(null);
    setError(null);
    try {
      const clubResponse = await axios.get(
        `${ApiConnectionString}/api/myclub/${id}`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      if (clubResponse.status === 200) {
        setClub(clubResponse.data);

        if (clubResponse.data?.premiumId) {
          const subResponse = await axios.get(
            `${ApiConnectionString}/api/getsub/${clubResponse.data.premiumId}`,
            {
              headers: { Authorization: `Bearer ${user.token}` },
            }
          );
          if (subResponse.status === 200) {
            setSub(subResponse.data);
          }
        }
      }
    } catch (error) {
      console.error(error);
      setError("Could not load data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  const handleChangePlan = async (tier) => {
    if (!club?.premiumId || sub?.isCanceled) return;
    setIsChanging(true);
    setMessage(null);
    setError(null);
    try {
      const response = await axios.put(
        `${ApiConnectionString}/api/upgrade`,
        {
          subscriptionId: club.premiumId,
          tier: tier,
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      const updatedSub = response.data;
      if (updatedSub) {
        setSub(updatedSub);
      } else {
        await fetchData();
      }

      setMessage(
        tier === "widgit"
          ? "Upgraded to Membotic Embedded."
          : "Downgraded to Membotic Standard."
      );
    } catch (e) {
      console.error(e);
      setError("Could not change plan. Please try again.");
    } finally {
      setIsChanging(false);
      setShowConfirm(false);
      setPendingTier(null);
    }
  };

  const askConfirm = (tier) => {
    setPendingTier(tier);
    setShowConfirm(true);
  };

  if (isLoading) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  if (!sub) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="col-md-10 col-lg-8">
          <div className="alert alert-warning">
            No subscription found for this club.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex justify-content-center mt-5">
      <div className="col-md-10 col-lg-8">
        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {message && sub && (
          <div className="alert alert-info mt-3">
            <h5 className="mb-1">New subscription details</h5>
            <p className="mb-0">
              Plan:{" "}
              {sub.unitAmount === 1000
                ? "Membotic Standard Plan"
                : "Membotic Embedded Plan"}{" "}
              • Price: £{(sub.unitAmount / 100).toFixed(2)} per month
            </p>
          </div>
        )}

        {sub.isCanceled && (
          <div className="alert alert-danger mt-3">
            This subscription is canceled. You cannot upgrade or downgrade.
          </div>
        )}

        {sub.unitAmount === 1000 ? (
          <div className="card shadow-sm border-0 p-4 rounded-4 mt-3">
            <div className="d-flex align-items-center mb-4">
              <Gem className="text-primary fs-3 me-2" />
              <h3 className="fw-bold mb-0">Your Current Plan</h3>
            </div>

            <div className="mb-3">
              <h5 className="fw-semibold text-dark">
                <BoxSeam className="me-2 text-success" />
                Membotic Standard Plan
              </h5>
              <p className="text-muted">
                The <strong>Standard Plan</strong> costs{" "}
                <strong>£10 per month</strong>. It includes all core Membotic
                features inside the app such as class scheduling, member
                handling, subscriptions and payg tools.
              </p>
            </div>

            <hr />

            <div className="mb-4">
              <h6 className="fw-semibold mb-2">Included features:</h6>
              <ul className="list-unstyled text-muted mb-0">
                <li className="mb-2">
                  <CheckCircle className="text-success me-2" />
                  Full in app club management
                </li>
                <li className="mb-2">
                  <CheckCircle className="text-success me-2" />
                  Member and class scheduling
                </li>
                <li className="mb-2">
                  <CheckCircle className="text-success me-2" />
                  Subscription and payg tools
                </li>
              </ul>
            </div>

            <div className="alert alert-primary mb-4">
              <ArrowUpCircle className="me-2" />
              Upgrade to <strong>Membotic Embedded</strong> for{" "}
              <strong>£15 per month</strong> to enable the website embed tool.
              This allows your timetable and payment options to appear directly
              on your own website.
            </div>

            <button
              className="btn btn-primary px-4 py-2"
              onClick={() => askConfirm("widgit")}
              disabled={isChanging || sub.isCanceled}
            >
              {isChanging && pendingTier === "widgit" ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
              ) : (
                <ArrowUpCircle className="me-2" />
              )}
              Upgrade to Membotic Embedded
            </button>
          </div>
        ) : (
          <div className="card shadow-sm border-0 p-4 rounded-4 mt-3">
            <div className="d-flex align-items-center mb-4">
              <Gem className="text-warning fs-3 me-2" />
              <h3 className="fw-bold mb-0">Your Current Plan</h3>
            </div>

            <div className="mb-3">
              <h5 className="fw-semibold text-dark">
                <Globe className="me-2 text-warning" />
                Membotic Embedded Plan
              </h5>
              <p className="text-muted">
                The <strong>Membotic Embedded Plan</strong> costs{" "}
                <strong>£15 per month</strong>. It includes everything in the
                Standard Plan plus the full website embed system. This lets you
                place your <strong>timetable</strong> and{" "}
                <strong>payment options</strong> directly on your own website so
                visitors can browse your classes and book without leaving your
                site.
              </p>
            </div>

            <hr />

            <div className="mb-4">
              <h6 className="fw-semibold mb-2">Included features:</h6>
              <ul className="list-unstyled text-muted mb-0">
                <li className="mb-2">
                  <CheckCircle className="text-success me-2" />
                  Everything in the Standard Plan
                </li>
                <li className="mb-2">
                  <CheckCircle className="text-success me-2" />
                  Website embed for timetable and bookings
                </li>
                <li className="mb-2">
                  <CheckCircle className="text-success me-2" />
                  Direct website integration for payments
                </li>
              </ul>
            </div>

            <div className="alert alert-secondary mb-4">
              <ArrowDownCircle className="me-2" />
              Downgrade to the <strong>Standard Plan</strong> for{" "}
              <strong>£10 per month</strong> if you only want to manage your
              club inside the Membotic app.
            </div>

            <button
              className="btn btn-secondary px-4 py-2"
              onClick={() => askConfirm("standard")}
              disabled={isChanging || sub.isCanceled}
            >
              {isChanging && pendingTier === "standard" ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
              ) : (
                <ArrowDownCircle className="me-2" />
              )}
              Downgrade to Membotic Standard
            </button>
          </div>
        )}

        {showConfirm && (
          <>
            <div className="modal-backdrop fade show"></div>
            <div className="modal d-block" tabIndex={-1}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Are you sure?</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowConfirm(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    {pendingTier === "widgit"
                      ? "You are about to upgrade to Membotic Embedded for £15 per month. This will enable the website embed so your timetable and payment options can appear on your own website."
                      : "You are about to downgrade to Membotic Standard for £10 per month. This will remove the website embed and you will manage everything inside the Membotic app."}
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowConfirm(false)}
                      disabled={isChanging}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleChangePlan(pendingTier)}
                      disabled={isChanging}
                    >
                      {isChanging ? "Please wait..." : "Confirm"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
